// src/components/Admin/ManageGallery.js
import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../../firebase';
import Overlay from '../Overlay';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';


function ManageGallery() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [formState, setFormState] = useState({
    title: '',
    description: '',
    imageFile: null,
    imageURL: '',
    id: null, // 編集時に使用
  });
  const [preview, setPreview] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);

  // トリミング用の状態
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    try {
      const snapshot = await db.collection("gallery").orderBy("date", "desc").get();
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setGalleryItems(data);
    } catch (error) {
      console.error("Error fetching gallery items:", error);
      alert('ギャラリーアイテムの取得に失敗しました。');
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    setFormState(prev => ({ ...prev, imageFile: file }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setIsCropping(true); // トリミングモードに切り替え
      };
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
      setIsCropping(false);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const showCroppedImage = useCallback(async () => {
    try {
      const { blob, fileUrl } = await getCroppedImg(preview, croppedAreaPixels, zoom, 1);
      setCroppedImage(blob);
      setPreview(fileUrl); // プレビューをトリミング後の画像に更新
      setIsCropping(false);
    } catch (e) {
      console.error(e);
      alert('画像のトリミングに失敗しました。');
    }
  }, [preview, croppedAreaPixels, zoom]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (formState.id) {
      await updateGalleryItem();
    } else {
      await addGalleryItem();
    }
  };

  const addGalleryItem = async () => {
    try {
      if (!croppedImage) {
        alert('画像をアップロードしてトリミングしてください。');
        return;
      }

      const storageRef = storage.ref(`gallery/${Date.now()}_${formState.imageFile.name}`);
      await storageRef.put(croppedImage);
      const imageURL = await storageRef.getDownloadURL();

      await db.collection("gallery").add({
        title: formState.title,
        description: formState.description,
        imageURL,
        date: new Date(),
      });

      alert('ギャラリーアイテムが追加されました。');
      resetForm();
      fetchGalleryItems();
    } catch (error) {
      console.error("Error adding gallery item:", error);
      alert('ギャラリーアイテムの追加に失敗しました。');
    }
  };

  const updateGalleryItem = async () => {
    try {
      let imageURL = formState.imageURL;
      if (croppedImage) {
        const storageRef = storage.ref(`gallery/${Date.now()}_${formState.imageFile.name}`);
        await storageRef.put(croppedImage);
        imageURL = await storageRef.getDownloadURL();
      }

      await db.collection("gallery").doc(formState.id).update({
        title: formState.title,
        description: formState.description,
        imageURL,
        date: new Date(),
      });

      alert('ギャラリーアイテムが更新されました。');
      resetForm();
      setShowOverlay(false);
      fetchGalleryItems();
    } catch (error) {
      console.error("Error updating gallery item:", error);
      alert('ギャラリーアイテムの更新に失敗しました。');
    }
  };

  const deleteGalleryItem = async (id, imageURL) => {
    if (!window.confirm('このギャラリーアイテムを削除してもよろしいですか？')) return;
    try {
      await db.collection("gallery").doc(id).delete();
      if (imageURL) {
        const storageRef = storage.refFromURL(imageURL);
        await storageRef.delete();
      }
      alert('ギャラリーアイテムが削除されました。');
      fetchGalleryItems();
    } catch (error) {
      console.error("Error deleting gallery item:", error);
      alert('ギャラリーアイテムの削除に失敗しました。');
    }
  };

  const editGalleryItem = item => {
    setFormState({
      title: item.title,
      description: item.description,
      imageFile: null,
      imageURL: item.imageURL || '',
      id: item.id,
    });
    setPreview(item.imageURL || null);
    setCroppedImage(null);
    setIsCropping(false);
  };

  const viewGalleryItem = item => {
    setFormState({
      title: item.title,
      description: item.description,
      imageFile: null,
      imageURL: item.imageURL || '',
      id: item.id,
    });
    setShowOverlay(true);
  };

  const resetForm = () => {
    setFormState({
      title: '',
      description: '',
      imageFile: null,
      imageURL: '',
      id: null,
    });
    setPreview(null);
    setCroppedImage(null);
    setIsCropping(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  return (
    <div className="manage-gallery">
      <h2>ギャラリーの管理</h2>
      <form onSubmit={handleSubmit}>
        <label>
          タイトル:
          <input 
            type="text" 
            name="title" 
            value={formState.title} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          説明:
          <textarea 
            name="description" 
            value={formState.description} 
            onChange={handleInputChange} 
            required 
          />
        </label>
        <label>
          画像:
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleFileChange} 
            required={!formState.id} // 編集時は必須ではない
          />
        </label>

        {isCropping && preview && (
          <div className="crop-container">
            <Cropper
              image={preview}
              crop={crop}
              zoom={zoom}
              aspect={1} // 正方形
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            <div className="controls">
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={(e, zoom) => setZoom(zoom)}
                style={{ width: '200px' }}
              />
              <Button variant="contained" color="primary" onClick={showCroppedImage} style={{ marginLeft: '10px' }}>
                トリミング
              </Button>
            </div>
          </div>
        )}

        {preview && !isCropping && (
          <div className="preview">
            <p>プレビュー:</p>
            <img 
              src={preview} 
              alt="Preview" 
              style={{ 
                width: '300px', 
                height: '300px', 
                objectFit: 'cover', 
                border: '2px solid #ccc',
                borderRadius: '0' // 丸くしない
              }} 
            />
          </div>
        )}

        <Button type="submit" variant="contained" color="primary" className="button">
          {formState.id ? 'ギャラリーアイテムを更新' : 'ギャラリーアイテムを追加'}
        </Button>
        {formState.id && (
          <Button 
            type="button" 
            variant="outlined" 
            color="secondary" 
            onClick={resetForm}
            className="button"
            style={{ marginLeft: '10px' }}
          >
            キャンセル
          </Button>
        )}
      </form>

      <h3>既存のギャラリーアイテム</h3>
      <ul>
        {galleryItems.map(item => (
          <li key={item.id} className="admin-list-item">
            {item.imageURL && <img src={item.imageURL} alt={item.title} style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '0' }} />}
            <div>
              <h4>{item.title}</h4>
              <p>{new Date(item.date.seconds * 1000).toLocaleDateString()}</p>
              <Button onClick={() => viewGalleryItem(item)} variant="outlined" color="primary" style={{ marginRight: '10px' }}>
                プレビュー
              </Button>
              <Button onClick={() => editGalleryItem(item)} variant="outlined" color="secondary" style={{ marginRight: '10px' }}>
                編集
              </Button>
              <Button onClick={() => deleteGalleryItem(item.id, item.imageURL)} variant="outlined" color="error">
                削除
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {showOverlay && (
        <Overlay onClose={() => setShowOverlay(false)}>
          <h3>{formState.title}</h3>
          {formState.imageURL && <img src={formState.imageURL} alt={formState.title} style={{ width: '300px', height: '300px', objectFit: 'cover', marginBottom: '1rem', borderRadius: '0' }} />}
          <p>{formState.description}</p>
        </Overlay>
      )}
    </div>
  );
}

export default ManageGallery;
